import { z } from 'zod'

import { MODULE_KEYS } from '@/modules/module-keys'

/* ==========================================================================
   Schemi
   --------------------------------------------------------------------------
   Ogni risposta viene validata a runtime prima di entrare nell'applicazione.
   Oggi la controparte è l'adapter mock e la validazione sembra superflua;
   quando arriverà il backend vero sarà l'unica cosa che impedisce a un campo
   rinominato di propagarsi in venti componenti prima di dare errore.
   ========================================================================== */

export const moduleKeySchema = z.enum(MODULE_KEYS)

/**
 * Un valore di metadato. Ogni azienda porta campi suoi, quindi qui non c'è
 * nessun elenco di chiavi: è il registro (`catalogSchemaSchema`) a dire quali
 * esistono, come si chiamano e di che tipo sono.
 */
export const metaValueSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])
export type MetaValue = z.infer<typeof metaValueSchema>

export const metaSchema = z.record(z.string(), metaValueSchema)
export type Meta = z.infer<typeof metaSchema>

/** Un elemento a catalogo. */
export const catalogItemSchema = z.object({
  id: z.string(),
  /** Tipo di contenuto: oggi solo `image`, domani anche documenti. */
  type: z.string(),
  /**
   * I metadati dell'azienda, già ridotti dal server a quelli da mostrare. Le
   * chiavi cambiano da cliente a cliente: si leggono col registro, mai cablate
   * dentro un componente.
   */
  meta: metaSchema,
  /** URL dell'immagine, firmato e temporaneo. */
  imageUrl: z.string(),
})
export type CatalogItem = z.infer<typeof catalogItemSchema>

/**
 * Risposta paginata di /search.
 *
 * `total` è quanti elementi passano il FILTRO, non quanti ne restituisce la
 * ricerca: per una ricerca per somiglianza "numero di risultati" non esiste —
 * si vedono i più simili, non quelli che corrispondono. `nextCursor` è opaco
 * al client: va solo rispedito così com'è per ottenere la pagina dopo.
 */
export const catalogSearchResponseSchema = z.object({
  items: z.array(catalogItemSchema),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
})
export type CatalogSearchResponse = z.infer<typeof catalogSearchResponseSchema>

/* --------------------------------------------------------------------------
   Il registro dei metadati dell'azienda (GET /api/catalog/schema)
   --------------------------------------------------------------------------
   È quello che permette all'interfaccia di costruire filtri e scheda senza
   avere nessun nome di campo cablato — l'unico modo di servire clienti che
   portano metadati diversi l'uno dall'altro.
   -------------------------------------------------------------------------- */

export const catalogFieldTypeSchema = z.enum([
  'keyword',
  'integer',
  'number',
  'bool',
  'datetime',
  'text',
])
export type CatalogFieldType = z.infer<typeof catalogFieldTypeSchema>

export const catalogFieldSchema = z.object({
  key: z.string(),
  /** Come lo chiama il cliente: si mostra questo, mai `key`. */
  label: z.string(),
  type: catalogFieldTypeSchema,
  /** Più valori per elemento (etichette, colori): il filtro diventa "contiene". */
  multi: z.boolean(),
  filterable: z.boolean(),
  display: z.boolean(),
  /** Valori noti, per le tendine. Assente quando sono troppi o non enumerabili. */
  values: z.array(z.string()).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
})
export type CatalogField = z.infer<typeof catalogFieldSchema>

export const catalogTypeSchema = z.object({
  label: z.string(),
  /**
   * Quale metadato fa da etichetta dell'elemento. `null` finché l'azienda non
   * ne dichiara uno, e allora si mostra l'id.
   */
  labelField: z.string().nullable(),
  fields: z.array(catalogFieldSchema),
})
export type CatalogType = z.infer<typeof catalogTypeSchema>

export const catalogSchemaSchema = z.object({
  schemaVersion: z.number().int().nonnegative(),
  types: z.record(z.string(), catalogTypeSchema),
})
export type CatalogSchema = z.infer<typeof catalogSchemaSchema>

export const collectionItemSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  type: z.string(),
  meta: metaSchema,
  imageUrl: z.string(),
  addedAt: z.string(),
})
export type CollectionItem = z.infer<typeof collectionItemSchema>

export const collectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  items: z.array(collectionItemSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Generata da un modulo e in attesa di conferma umana. */
  draft: z.boolean(),
})
export type Collection = z.infer<typeof collectionSchema>

export const proposalViewSchema = z.object({
  itemId: z.string(),
  /** L'etichetta umana risolta dal server: il metadato designato, o l'id. */
  label: z.string(),
  views: z.number().int().nonnegative(),
})
export type ProposalView = z.infer<typeof proposalViewSchema>

export const shareDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  collectionId: z.string(),
  url: z.string(),
  createdAt: z.string(),
  views: z.number().int().nonnegative(),
  averageMinutes: z.number().nonnegative(),
  proposalViews: z.array(proposalViewSchema),
})
export type ShareDocument = z.infer<typeof shareDocumentSchema>

export const experienceStatusSchema = z.enum(['generating', 'ready'])
export type ExperienceStatus = z.infer<typeof experienceStatusSchema>

export const experience3dSchema = z.object({
  id: z.string(),
  name: z.string(),
  collectionId: z.string(),
  url: z.string(),
  createdAt: z.string(),
  status: experienceStatusSchema,
})
export type Experience3d = z.infer<typeof experience3dSchema>

export const searchModeSchema = z.enum(['testo', 'immagine'])
export type SearchMode = z.infer<typeof searchModeSchema>

/**
 * Regione ritagliata su un'immagine di riferimento, in frazioni del riquadro
 * (0–1) anziché in pixel: resta valida a qualunque risoluzione la si guardi.
 */
export const cropRectSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1),
})
export type CropRect = z.infer<typeof cropRectSchema>

/**
 * Ricerca a partire da un'immagine portata da chi cerca.
 *
 * `image` ha due forme perché le due provenienze hanno vincoli diversi: una
 * `data:` URL per un file scelto o trascinato, di cui il browser può leggere e
 * ridurre i byte; l'indirizzo originale per un'immagine presa da un link, di
 * cui il browser può mostrare i pixel ma non leggerli (CORS contamina il
 * canvas). Il ritaglio viaggia come rettangolo normalizzato sull'immagine
 * intera, non come byte già tagliati: tagliare a monte perderebbe
 * risoluzione e impedirebbe di riprovare con un ritaglio diverso.
 */
export const imageSearchRequestSchema = z.object({
  image: z.string().min(1),
  crop: cropRectSchema,
  cursor: z.string().optional(),
  type: z.string().optional(),
  /**
   * Gli stessi filtri della ricerca testuale, qui nel corpo invece che in
   * query string. Le chiavi sono quelle del registro dell'azienda.
   */
  filters: z
    .record(
      z.string(),
      z.union([
        z.string(),
        z.array(z.string()),
        z.object({ from: z.number().optional(), to: z.number().optional() }),
      ]),
    )
    .optional(),
})
export type ImageSearchRequest = z.infer<typeof imageSearchRequestSchema>

export const imageSearchResponseSchema = catalogSearchResponseSchema.extend({
  /**
   * `false` finché il server non ordina per somiglianza vera: gli embedding
   * vivranno in Qdrant, che non è ancora costruito. Il client lo legge per non
   * promettere "i più simili" quando l'ordine è quello del catalogo.
   */
  ranked: z.boolean().default(false),
})
export type ImageSearchResponse = z.infer<typeof imageSearchResponseSchema>

export const searchHistoryEntrySchema = z.object({
  id: z.string(),
  mode: searchModeSchema,
  label: z.string(),
  createdAt: z.string(),
  resultCount: z.number().int().nonnegative(),
})
export type SearchHistoryEntry = z.infer<typeof searchHistoryEntrySchema>

export const entitlementSchema = z.object({
  module: moduleKeySchema,
  active: z.boolean(),
  /** Null se il modulo non è mai stato attivato. */
  activatedAt: z.string().nullable(),
})
export type Entitlement = z.infer<typeof entitlementSchema>

export const creditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  module: moduleKeySchema,
  cost: z.number().int().positive(),
  createdAt: z.string(),
})
export type CreditEntry = z.infer<typeof creditEntrySchema>

export const creditBalanceSchema = z.object({
  used: z.number().int().nonnegative(),
  included: z.number().int().positive(),
  ledger: z.array(creditEntrySchema),
})
export type CreditBalance = z.infer<typeof creditBalanceSchema>

export const activityEntrySchema = z.object({
  id: z.string(),
  message: z.string(),
  module: moduleKeySchema,
  createdAt: z.string(),
})
export type ActivityEntry = z.infer<typeof activityEntrySchema>

export const recommendationSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  meta: metaSchema,
  imageUrl: z.string(),
  /** Perché è stato proposto: si mostra a chi decide, non si tiene nascosto. */
  reason: z.string(),
})
export type Recommendation = z.infer<typeof recommendationSchema>

export const listSchema = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item) })

export const sessionSchema = z.object({
  token: z.string(),
  user: z.object({ username: z.string() }),
})
export type Session = z.infer<typeof sessionSchema>

/** Il profilo azienda dell'utente autenticato (endpoint /me). */
export const meSchema = z.object({
  username: z.string(),
  companyName: z.string(),
  email: z.string(),
  role: z.string(),
  plan: z.string().nullable(),
  createdAt: z.string(),
  lastLogin: z.string().nullable(),
})
export type Me = z.infer<typeof meSchema>

/** Una riga della tabella companies, per il pannello Aziende. */
export const companySchema = meSchema.extend({ id: z.string() })
export type Company = z.infer<typeof companySchema>

export const companyPatchSchema = z.object({
  companyName: z.string().min(1).optional(),
  role: z.string().min(1).optional(),
  plan: z.string().nullable().optional(),
})
export type CompanyPatch = z.infer<typeof companyPatchSchema>
